import type { FC } from 'react'
import { ProvidersUserPeopleRetrieveResponse } from '@fireflyhealth/api'
import { Button, Box, Grid, Switch, Typography } from '@mui/material'
import { useFormik } from 'formik'

import { EditField } from '~/shared/ui'
import EditPersonFields from './EditPersonFields'
import { useFeatureAccess } from '~/shared/hooks/useFeatureAccess'
import { ValueSetter } from '~/shared/types'

interface EditPersonFormProps {
  patient: ProvidersUserPeopleRetrieveResponse
  loading: boolean
  isNewMember?: boolean
  handleSubmit: (values: ProvidersUserPeopleRetrieveResponse) => void | Promise<void>
}

const EditPersonForm: FC<EditPersonFormProps> = props => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: props.patient,
    onSubmit: props.handleSubmit,
  })
  const featureAccess = useFeatureAccess()?.tenant

  const {
    first_name: firstName = '',
    last_name: lastName = '',
    dob = '',
    preferred_name: preferredName = '',
    employer = null,
    gender = null,
    sex = 'Unknown',
    pronouns = null,
    request_auth_for_phi: requestAuthForPhi = false,
    phone_number: phoneNumber = '',
    email = '',
  } = formik.values

  // Shortcut for change handlers and values
  const setValues: ValueSetter = (name, value) => ({
    name,
    onChange: e => {
      e.persist()
      formik.handleChange(e)
      void formik.setFieldTouched(name, true, false)
    },
    disabled: props.loading,
    value,
  })

  const submitButtonText = props.isNewMember ? 'Add New Person' : 'Save Person Details'
  return (
    <form onSubmit={formik.handleSubmit}>
      <EditPersonFields
        first_name={firstName ?? ''}
        last_name={lastName ?? ''}
        dob={dob ?? ''}
        preferred_name={preferredName ?? ''}
        employer={employer ?? null}
        gender={Array.isArray(gender) ? gender.filter((g): g is string => g != null) : null}
        sex={sex ?? 'Unknown'}
        pronouns={pronouns ?? null}
        loading={props.loading}
        isUser={false}
        setValues={setValues}
        setFieldValue={formik.setFieldValue}
        setFieldTouched={name => void formik.setFieldTouched(name)}
        isNewMember={props.isNewMember}
        patientId={props.patient.id}
        memberIdentifierType={props.patient.member_identifier_type}
      />
      <Grid container spacing={2}>
        {/* Disabling for now. will re-enable once email updation on person is fixed,
        as currently using email on person serializer in api is causing issues with
        existing patient profile update */}
        <EditField
          {...setValues('email', email)}
          label="Email"
          required={featureAccess?.person_matching_during_signup}
        />
        {/* Phone number is sourced from Auth0 MFA via the phone sync paths
            (see `Person.phone_number` model comment); `PersonCRUDSerializer`
            marks it read-only and drops any value sent on PATCH. Render
            disabled so providers see the current value without a misleading
            edit affordance. */}
        <EditField
          {...setValues('phone_number', phoneNumber)}
          label="Phone Number"
          required={featureAccess?.person_matching_during_signup}
          disabled
        />
      </Grid>

      {/* `request_auth_for_phi` is the PHI access guard and read-only on every
          Person serializer; not editable from CRM. Render disabled so the
          current state is visible without a misleading edit affordance. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3 }}>
        <Switch
          name="request_auth_for_phi"
          edge="start"
          checked={!!requestAuthForPhi}
          color="primary"
          disabled
        />
        <Box sx={{ ml: 1 }}>
          <Typography variant="body2">Patient is VIP</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 3 }}>
        {formik.dirty && (
          <Box sx={{ mr: 3 }}>
            <Button onClick={() => formik.resetForm()}>Cancel Person Detail Changes</Button>
          </Box>
        )}

        <Button type="submit" variant="outlined" color="primary">
          {submitButtonText}
        </Button>
      </Box>
    </form>
  )
}

export default EditPersonForm
